import { useCallback, useEffect, useMemo } from 'react';

import { compareRecords } from '../utils/sort';
import { getItemPrimaryDate } from '../utils/items';
import { PRIMARY_DATE_SORT } from '../utils/cards';
import { sameSortThen, sortThenOf, sortThenPatch } from '../utils/sortLevels';

// The primary date leads: the API resolves the token per card (each item's
// card's created / updated / date element — docs/plans/primary-date.md), so
// it is the default and is sent as-is; an API that predates it falls back to
// createdAt desc, never an error.
export const BASE_SORT_OPTIONS = [
	{ value: PRIMARY_DATE_SORT, label: 'Primary date', dataType: 'date-time' },
	{ value: 'createdAt', label: 'Created date', dataType: 'date-time' },
	{ value: 'updatedAt', label: 'Updated date', dataType: 'date-time' },
];

const SYSTEM_SORTS = ['primaryDate', 'createdAt', 'updatedAt'];

/**
 * The order a list is asked for, BEFORE its feed is fetched: what the address
 * says, unless a table layout has a default order for the one card in view.
 *
 * A table layout may carry a default ORDER for its card. It applies only where
 * the URL names that one card (so the feed's sort is known before the feed is
 * fetched) and the reader has not chosen an order themselves; a field the card
 * no longer has is ignored rather than sent.
 *
 * `{ sortField, sortDirection, sortThen, layoutSort }` - `layoutSort` is the
 * layout's order when it is the one in force, else null. `then` / `sortThen`
 * are the further levels ("then by", KPR-1636; utils/sortLevels): what the
 * address says, or none while a layout's order is in force (a layout carries
 * one level, and a reader who adds one has chosen an order).
 */
export function useListSortOrder({
	field,
	direction,
	then,
	chosen = false,
	tableOn = false,
	cardFilter,
	tableLayouts,
	getElementsForCard,
}) {
	const layoutSort = useMemo(() => {
		if (!tableOn || !cardFilter || chosen) return null;
		const sort = tableLayouts?.[cardFilter]?.body?.sort;
		if (!sort?.field) return null;
		const system = SYSTEM_SORTS.includes(sort.field);
		if (!system && !(getElementsForCard(cardFilter) || []).some(el => el?.name === sort.field)) return null;
		return { field: sort.field, direction: sort.direction === 'asc' ? 'asc' : 'desc' };
	}, [tableOn, cardFilter, chosen, tableLayouts, getElementsForCard]);
	// One identity while the levels say the same, so the feed does not restart.
	const thenKey = JSON.stringify(sortThenOf(then));
	const sortThen = useMemo(() => (layoutSort ? [] : JSON.parse(thenKey)), [layoutSort, thenKey]);
	return {
		sortField: layoutSort ? layoutSort.field : field,
		sortDirection: layoutSort ? layoutSort.direction : direction,
		sortThen,
		layoutSort,
	};
}

const NO_LEVELS = [];

/**
 * One select carries field AND direction ("Newest first"). Date-ish fields read
 * as newest/oldest; a "Me too" count (KPR-390) as most / fewest — the question
 * a list of requests is sorted to answer; a repeating group as most / fewest
 * entries (KPR-683); everything else as A→Z / Z→A.
 */
export function combineSortOptions(sortOptions) {
	return sortOptions.flatMap(opt => {
		const isDate = opt.dataType === 'date' || opt.dataType === 'date-time';
		const isCount = opt.dataType === 'confirmations';
		// A repeating group sorts by how many entries it holds (KPR-683).
		const isEntries = opt.dataType === 'repeating-group';
		const isBase = BASE_SORT_OPTIONS.some(base => base.value === opt.value);
		const descLabel = isBase && opt.value === 'createdAt' ? 'Newest first'
			: isBase && opt.value === 'updatedAt' ? 'Recently updated'
			: `${opt.label} ${isDate ? '(newest)' : isEntries ? '(most entries)' : isCount ? '(most)' : '(Z–A)'}`;
		const ascLabel = isBase && opt.value === 'createdAt' ? 'Oldest first'
			: isBase && opt.value === 'updatedAt' ? 'Least recently updated'
			: `${opt.label} ${isDate ? '(oldest)' : isEntries ? '(fewest entries)' : isCount ? '(fewest)' : '(A–Z)'}`;
		return [
			{ value: `${opt.value}:desc`, label: descLabel },
			{ value: `${opt.value}:asc`, label: ascLabel },
		];
	});
}

/**
 * The sort of a list of items: what it can be sorted by, the change handler,
 * and the fallback ordering of the rows in hand. Called after the feed (the
 * options depend on which card the rows are), with the order
 * `useListSortOrder` gave the feed.
 *
 *   const order = useListSortOrder({ ... });      // before the feed
 *   const feed = useListFilter({ ..., sortField: order.sortField, ... });
 *   const sort = useListSort({ order, writeSort, cardId, items: feed.items, ... });
 *   <SortSelect {...sort.selectProps} />          // one select (a related list)
 *   <SortLevels {...sort.levelsProps} />          // the items page's panel (KPR-1637)
 *
 * - `cardId`: the one card in view (an explicit filter, or a feed that holds
 *   only one). Element-based options only make sense when every visible item
 *   is the same card; without it the list sorts by the dates alone.
 * - `writeSort(patch, { keepDefaults })` writes the choice where the list keeps
 *   it: `{ sort, dir }` for a choice, `{ sort: undefined }` to fall back to
 *   the default. Under a layout's default order the choice is written out
 *   loud, because an absent value would hand the choice straight back to it.
 * - `levelsProps.onChangeFirst(field | null, direction)`: a table header's
 *   click (KPR-1638); see its comment below.
 * - `chosen`: whether the reader has chosen an order (false = the default is in
 *   force); it only decides what `levelsProps.source` says.
 * - `ready`: false while the card definitions are still loading. A level that
 *   names an element is judged only once they have: before that the card has
 *   no elements to offer, and a shared or reloaded link's sort would be reset
 *   to the default as it arrived (KPR-1637).
 * - A sort that names something not offered — an element the person may not
 *   see, a removed one, a card no longer in view — is reset to the default,
 *   never sent.
 */
export default function useListSort({
	order,
	writeSort,
	cardId,
	items,
	getElementsForCard,
	cardPrimaryDate,
	chosen,
	ready = true,
}) {
	const { sortField, sortDirection, layoutSort } = order;
	const sortThen = order.sortThen || NO_LEVELS;

	const sortOptions = useMemo(() => {
		// One card in view: say which date its primary date is.
		const options = BASE_SORT_OPTIONS.map(opt => (
			opt.value === PRIMARY_DATE_SORT && cardId
				? { ...opt, label: `${opt.label} · ${cardPrimaryDate(cardId).label}` }
				: opt
		));
		if (cardId) {
			getElementsForCard(cardId).forEach(el => {
				if (!el?.name) return;
				// A list never sorts by a file element (PR 10b): its value is
				// an attachment id, which has no order anyone means.
				if (el.dataType === 'file') return;
				options.push({
					value: el.name,
					label: el.label?.singular || el.name,
					dataType: el.dataType || 'text-small',
					// A measurement sorts on its base; the comparator needs the
					// element's measure to read one.
					options: el.options || {},
				});
			});
		}
		return options;
	}, [getElementsForCard, cardPrimaryDate, cardId]);

	// A level that names something not offered is reset, never sent: level 1
	// to the default, a further level dropped (the rest move up). ONE write,
	// because a second update in the same effect would replace the first.
	useEffect(() => {
		if (!ready) return;
		const offered = value => sortOptions.some(opt => opt.value === value);
		const patch = {};
		if (!offered(sortField)) patch.sort = undefined;
		const kept = sortThen.filter(level => offered(level.field));
		if (kept.length !== sortThen.length) Object.assign(patch, sortThenPatch(kept));
		if (Object.keys(patch).length) writeSort(patch);
	}, [ready, sortOptions, sortField, sortThen, writeSort]);

	const sortMeta = sortOptions.find(option => option.value === sortField) || BASE_SORT_OPTIONS[0];
	// Each further level with its option, for the fallback ordering below (a
	// level not offered is about to be dropped, and orders nothing meanwhile).
	const sortThenKeys = useMemo(
		() => sortThen
			.map(level => ({ ...level, meta: sortOptions.find(option => option.value === level.field) }))
			.filter(level => level.meta),
		[sortThen, sortOptions]
	);

	const combinedSortOptions = useMemo(() => combineSortOptions(sortOptions), [sortOptions]);

	const onChange = useCallback((value) => {
		const splitAt = value.lastIndexOf(':');
		writeSort({ sort: value.slice(0, splitAt), dir: value.slice(splitAt + 1) }, { keepDefaults: Boolean(layoutSort) });
	}, [writeSort, layoutSort]);

	// The further levels ("then by", KPR-1636): `levels` replaces them whole
	// ([] is one level again). Under a layout's order, level 1 is written out
	// loud in the same write: a further level is a choice, and the layout's
	// order would otherwise give way to the address's (the default) and the
	// list would jump under the person's hand.
	const onChangeThen = useCallback((levels) => {
		const next = sortThenOf(levels);
		// Nothing to write: the same levels, or none asked for under a layout's
		// order (which already has none, and is not to be pinned for nothing).
		if (sameSortThen(next, sortThen) && (!layoutSort || !next.length)) return;
		writeSort({
			...(layoutSort ? { sort: layoutSort.field, dir: layoutSort.direction } : {}),
			...sortThenPatch(next),
		}, { keepDefaults: Boolean(layoutSort) });
	}, [writeSort, layoutSort, sortThen]);

	// A table header's click (KPR-1638): level 1 alone, or back to the default
	// (`field` null: `sort` and `dir` leave the address, so a table layout's
	// order or the primary date applies). The further levels stay as they are,
	// except one naming the same field, which is dropped (each field once, as the
	// Sort panel offers), all in ONE write. Under a layout's order the choice is
	// written out loud, as `onChange` does.
	const onChangeFirst = useCallback((field, direction) => {
		const kept = field ? sortThen.filter(level => level.field !== field) : sortThen;
		writeSort({
			sort: field || undefined,
			dir: field ? direction : undefined,
			...(kept.length === sortThen.length ? {} : sortThenPatch(kept)),
		}, { keepDefaults: Boolean(field) && Boolean(layoutSort) });
	}, [writeSort, layoutSort, sortThen]);

	// An item's primary date is whichever field its OWN card chose.
	const primaryDateOf = useCallback(
		item => getItemPrimaryDate(item, cardPrimaryDate(item?.card_id)),
		[cardPrimaryDate]
	);
	const primaryDateGetter = sortField === PRIMARY_DATE_SORT ? primaryDateOf : undefined;

	// Fallback ordering for the loaded feed. The sort params are already
	// sent to the API (see useCollectionItems); once the server sorts, this
	// re-sort of an already-sorted list is a harmless no-op. The direction is
	// applied inside the comparator (never a reverse of the list): blanks stay
	// last and equal values stay in the order the server sent, as in the API.
	// Each further level breaks the ties of the one before; a full tie keeps
	// the server's order (Array.prototype.sort is stable).
	const sortedItems = useMemo(() => {
		const keys = [
			{ field: sortField, direction: sortDirection, meta: sortMeta },
			...sortThenKeys,
		].map(({ field, direction, meta }) => ({
			field,
			direction,
			dataType: meta?.dataType || 'text-small',
			options: meta?.options,
			getValue: field === PRIMARY_DATE_SORT ? primaryDateOf : undefined,
		}));
		const sorted = [...items];
		sorted.sort((a, b) => {
			for (const key of keys) {
				const order = compareRecords(a, b, key.field, key.dataType, key.options, key.getValue, key.direction);
				if (order) return order;
			}
			return 0;
		});
		return sorted;
	}, [items, sortDirection, sortField, sortMeta, sortThenKeys, primaryDateOf]);

	return {
		sortField,
		sortDirection,
		// The further levels in force, and the writer for them (KPR-1636).
		sortThen,
		onChangeThen,
		sortOptions,
		sortMeta,
		combinedSortOptions,
		onChange,
		primaryDateGetter,
		sortedItems,
		// What <SortSelect> takes.
		selectProps: { field: sortField, direction: sortDirection, options: combinedSortOptions, onChange },
		// What <SortLevels> takes (KPR-1637): the levels, the field options once
		// each, and where an order nobody chose comes from.
		levelsProps: {
			field: sortField,
			direction: sortDirection,
			sortThen,
			options: sortOptions,
			onChange,
			onChangeThen,
			onChangeFirst,
			source: layoutSort ? 'layout' : chosen === false ? 'default' : null,
		},
	};
}
